import { css, html, LitElement, PropertyValues, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { CalendarUtility } from './utils/calendar-utility';
import { DateUtility } from './utils/date-utility';
import { DerVariant, Direction } from './types';
import { CalendarMode, ClassBuilder, RangeDateChange, SelectOption, Sheets, SingleDateChange } from './types';
import mainStyles from './main.scss?inline';
import { LocaleCode } from '../../types/lang.types';

export enum CalendarTimeElementClass {
    Day = 'c-calendar__day',
    DayNumber = 'c-calendar__day-number',
    Today = 'c-calendar__day--today',
    Selected = 'c-calendar__day--selected',
    SelectedStart = 'c-calendar__day--selected-start',
    SelectedEnd = 'c-calendar__day--selected-end',
    Unselectable = 'c-calendar__day--unselectable',
    Flexible = 'c-calendar__day--flexible',
    FlexibleStart = 'c-calendar__day--flexible-start',
    FlexibleEnd = 'c-calendar__day--flexible-end',
    SelectedRange = 'c-calendar__day--selected-range',
}

export class Calendar extends LitElement {
    static override styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
    ];

    @property({ type: String })
    public lang = LocaleCode.DE_CH;

    @property({ type: String })
    public currency = '';

    @property({ type: String })
    public brand = '';

    @property({ type: String })
    public tenant = '';

    @property({ type: Number, attribute: 'booking-day-offset' }) bookingDayOffset = 0;
    @property({
        type: Number,
        attribute: 'sheets',
        converter: (value: string | null): 1 | 2 => (Number(value) === 2 ? 2 : 1),
    })
    sheets: typeof Sheets.Single | typeof Sheets.Double = Sheets.Single;
    @property({ type: String, attribute: 'flexible-end-date' }) flexibleEndDate = '';
    @property({ type: String, attribute: 'selected-end-date' }) selectedEndDate = '';
    @property({ type: String, attribute: 'flexible-start-date' }) flexibleStartDate = '';
    @property({ type: String, attribute: 'selected-start-date' }) selectedStartDate = '';
    @property({
        type: Boolean,
        attribute: 'month-selection-dropdown',
        converter: (value: string | null): boolean => value !== null && value !== 'false',
    })
    monthSelectionDropdown = false;
    @property({ type: Number, attribute: 'month-dropdown-limit' }) monthDropdownLimit = 16;
    @property({ type: String, attribute: 'mode' }) mode: CalendarMode = CalendarMode.Single;
    @property({ type: String, attribute: 'selected-date' }) selectedDate = '';
    @property({ type: Array, attribute: 'selected-dates' }) selectedDates: string[] = [];
    @property({ type: Array }) private openingWeekdays: number[] = [1, 2, 3, 4, 5, 6, 0];
    @property({ type: Array }) specialSelectableDates: string[] = [];

    @state() protected activeMonth: number;
    @state() protected activeYear: number;
    @state() private selecting: 'start' | 'end' = 'start';
    @state() private today = new Date();
    @state() private todayString = '';
    @state() private prevDisabled = false;
    @state() private nextDisabled = false;

    private todayUpdateTimer?: number;

    /**
     *
     */
    constructor() {
        super();
        const today = new Date();
        this.activeMonth = today.getMonth() + 1;
        this.activeYear = today.getFullYear();
        this.updateTodayCache();
    }

    reset() {
        if (this.selectedDate === '') {
            return;
        }

        this.selectedDate = '';

        this.dispatchEvent(
            new CustomEvent('der.change', {
                detail: {
                    mode: this.mode,
                    date: '',
                } as SingleDateChange,
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Updates the cached today date and today string.
     * Schedules the next update at midnight to keep the "today" indicator accurate.
     */
    private updateTodayCache() {
        this.today = new Date();
        this.todayString = DateUtility.getDateString(DateUtility.getIsoDate(this.today));

        const msUntilMidnight = DateUtility.getMillisecondsUntilMidnight(this.today);
        this.todayUpdateTimer = window.setTimeout(() => {
            this.updateTodayCache();
            this.requestUpdate();
        }, msUntilMidnight);
    }

    /**
     *
     */
    override connectedCallback() {
        super.connectedCallback();
        this.addEventListener('keydown', this.onKeydown);
        CalendarUtility.validateModeProperties(
            this.mode,
            this.selectedDate,
            this.selectedStartDate,
            this.selectedEndDate
        );
    }

    /**
     *
     */
    override disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('keydown', this.onKeydown);

        if (this.todayUpdateTimer) {
            clearTimeout(this.todayUpdateTimer);
            this.todayUpdateTimer = undefined;
        }
    }

    protected override willUpdate(changedProperties: PropertyValues) {
        if (changedProperties.has('mode')) {
            CalendarUtility.validateModeProperties(
                this.mode,
                this.selectedDate,
                this.selectedStartDate,
                this.selectedEndDate
            );
        }
    }

    protected override updated(changedProperties: PropertyValues) {
        if (changedProperties.has('selectedStartDate') && this.selectedStartDate && this.mode === CalendarMode.Range) {
            if (DateUtility.isValidDateString(this.selectedStartDate) && this.sheets === 1) {
                this.activeMonth = new Date(this.selectedStartDate).getMonth() + 1;
                this.activeYear = new Date(this.selectedStartDate).getFullYear();
            }
        }

        if (changedProperties.has('selectedDate') && this.selectedDate && this.mode === CalendarMode.Single) {
            if (DateUtility.isValidDateString(this.selectedDate) && this.sheets === 1) {
                this.activeMonth = new Date(this.selectedDate).getMonth() + 1;
                this.activeYear = new Date(this.selectedDate).getFullYear();
            }
        }
    }

    /**
     * Handles keyboard events for date selection in the calendar.
     * Processes Space and Enter key presses to select dates when focused on a calendar day.
     * Finds the closest time element, validates it's selectable, and triggers day click handler.
     * @param e Keyboard event from the user interaction
     */
    private onKeydown = (e: KeyboardEvent) => {
        if (e.code !== 'Space' && e.code !== 'Enter') {
            return;
        }

        const timeElement = (e.target as HTMLElement).closest('time') as HTMLTimeElement | null;
        if (timeElement?.dateTime) {
            try {
                const dateData = new Date(timeElement.dateTime);

                if (!timeElement.classList.contains(CalendarTimeElementClass.Unselectable)) {
                    this.handleDayClick(dateData.getDate(), dateData.getMonth() + 1, dateData.getFullYear());
                    e.preventDefault();
                }
            } catch {
                return;
            }
        }
    };

    /**
     * Handles day click events from the calendar.
     * Formats the clicked date and delegates to the appropriate handler based on mode.
     * @param day Day of the month (1-31)
     * @param month Month (1-12)
     * @param year Year (e.g., 2025)
     */
    private handleDayClick(day: number, month: number, year: number): void {
        const clickedDate = this._formatDate(day, month, year);

        if (!DateUtility.isValidDateString(clickedDate)) {
            console.error('Calendar: Invalid clicked date:', clickedDate);
            return;
        }

        if (this.mode === 'single') {
            this.handleSingleDateClick(clickedDate);
        } else {
            this.handleRangeDateClick(clickedDate);
        }
    }

    /**
     * Formats a date into YYYY-MM-DD string format.
     * @param day Day of the month (1-31)
     * @param month Month (1-12)
     * @param year Year (e.g., 2025)
     * @returns Formatted date string in YYYY-MM-DD format
     */
    private _formatDate(day: number, month: number, year: number): string {
        return year + '-' + DateUtility.addLeadingZero(month) + '-' + DateUtility.addLeadingZero(day);
    }

    /**
     * Handles single date selection in single mode.
     * Updates the selected date and dispatches a change event with the date in DD.MM.YYYY format.
     * @param clickedDate Selected date in YYYY-MM-DD format
     */
    private handleSingleDateClick(clickedDate: string): void {
        this.selectedDate = clickedDate;

        // Legacy variant
        this.dispatchEvent(
            new CustomEvent('der.change', {
                detail: {
                    mode: this.mode,
                    date: this.selectedDate.split('-').reverse().join('.'), // Convert ISO to German date string
                } as SingleDateChange,
                bubbles: true,
                composed: true,
            })
        );
        // end of legacy variant

        this.dispatchEvent(
            new CustomEvent('der-calendar-change', {
                detail: {
                    mode: this.mode,
                    date: this.selectedDate.split('-').reverse().join('.'), // Convert ISO to German date string
                } as SingleDateChange,
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Handles date selection in range mode.
     * Manages start and end date selection, swapping dates if end is before start.
     * @param clickedDate Selected date in YYYY-MM-DD format
     */
    private handleRangeDateClick(clickedDate: string): void {
        if (this.selecting === 'start') {
            this.selectedStartDate = clickedDate;
            this.selectedEndDate = clickedDate;
            this.selecting = 'end';
        } else if (this.selecting === 'end') {
            this.selectedEndDate = clickedDate;

            try {
                const start = new Date(
                    DateUtility.getYearAsNumber(this.selectedStartDate),
                    DateUtility.getMonthAsNumber(this.selectedStartDate) - 1,
                    DateUtility.getDayAsNumber(this.selectedStartDate)
                );
                const end = new Date(
                    DateUtility.getYearAsNumber(this.selectedEndDate),
                    DateUtility.getMonthAsNumber(this.selectedEndDate) - 1,
                    DateUtility.getDayAsNumber(this.selectedEndDate)
                );

                if (start > end) {
                    const oldSelectedStartDate = this.selectedStartDate;
                    this.selectedStartDate = this.selectedEndDate;
                    this.selectedEndDate = oldSelectedStartDate;
                }

                // Legacy variant
                this.dispatchEvent(
                    new CustomEvent('der.change', {
                        detail: {
                            mode: this.mode,
                            start: this.selectedStartDate,
                            end: this.selectedEndDate,
                        } as RangeDateChange,
                        bubbles: true,
                        composed: true,
                    })
                );
                // end of legacy variant

                this.dispatchEvent(
                    new CustomEvent('der-calendar-change', {
                        detail: {
                            mode: this.mode,
                            start: this.selectedStartDate,
                            end: this.selectedEndDate,
                        } as RangeDateChange,
                        bubbles: true,
                        composed: true,
                    })
                );
            } catch (error) {
                console.error('Calendar: Error processing range dates:', error);
            }

            this.selecting = 'start';
        }
    }

    private nextMonth = (): void => {
        this.activeMonth = this.activeMonth === 12 ? 1 : this.activeMonth + 1;
        this.activeYear = this.activeMonth === 1 ? this.activeYear + 1 : this.activeYear;
    };

    private prevMonth = (): void => {
        this.activeMonth = this.activeMonth === 1 ? 12 : this.activeMonth - 1;
        this.activeYear = this.activeMonth === 12 ? this.activeYear - 1 : this.activeYear;
    };

    protected dayRenderer = (day: number | null, isUnselectable: boolean): unknown => {
        return html`<span class="c-calendar__day-number" tabindex="${isUnselectable ? '-1' : '0'}">${day}</span>`;
    };

    /**
     * Renders a single day element in the calendar.
     * Creates a time element with appropriate classes, ARIA attributes, and event handlers.
     * @param day Day of the month (1-31) or null for empty cells
     * @param month Month (1-12)
     * @param year Year (e.g., 2025)
     * @returns Template result for the day element
     */
    private renderDay(day: number | null, month: number, year: number) {
        if (!day) {
            return html` <div></div>`;
        }

        try {
            const dayClassBuilder = this.getCustomTimeElementClassBuilders(day, month, year);
            const dayClasses = CalendarUtility.getClasses(dayClassBuilder);
            const dateValue = this._formatDate(day, month, year);
            const isUnselectable = dayClasses.includes(CalendarTimeElementClass.Unselectable);
            const isSelectedStart = dayClasses.includes(CalendarTimeElementClass.SelectedStart);
            const isSelectedEnd = dayClasses.includes(CalendarTimeElementClass.SelectedEnd);
            const isSelected = dayClasses.includes(CalendarTimeElementClass.Selected);
            const isToday = dayClasses.includes(CalendarTimeElementClass.Today);
            const showSelectedCircle = isSelectedStart || isSelectedEnd || isSelected;

            return html`
                <time
                    class="${dayClasses.join(' ')}"
                    role="gridcell"
                    aria-selected="${showSelectedCircle}"
                    aria-current="${isToday ? 'date' : 'false'}"
                    aria-label="${DateUtility.formatDateForScreenReader(day, month, year, this.lang)}"
                    datetime="${dateValue}"
                    @click="${() => !isUnselectable && this.handleDayClick(day, month, year)}"
                    @keydown="${this.onKeydown}"
                >
                    ${this.dayRenderer(day, isUnselectable)}
                </time>
            `;
        } catch {
            return html` <div class="c-calendar__day error" aria-label="Error rendering day">${day}</div>`;
        }
    }

    /**
     * Gets custom class builders for a calendar day element.
     * Determines classes for selection state, today, unselectable days, and range selection.
     * @param day Day of the month (1-31) or null for empty cells
     * @param month Month (1-12)
     * @param year Year (e.g., 2025)
     * @returns Array of class builders for the day element
     */
    getCustomTimeElementClassBuilders(day: number | null, month: number, year: number): ClassBuilder[] {
        if (!day) {
            return [];
        }
        const currentDate = this._formatDate(day, month, year);

        const isToday =
            day === this.today.getDate() && month === this.today.getMonth() + 1 && year === this.today.getFullYear();

        let isSelectedStart = false;
        let isSelectedEnd = false;
        let isSelected = false;
        let isInRange = false;

        if (this.mode === 'single') {
            isSelected = currentDate === this.selectedDate;
        } else {
            isInRange = DateUtility.safeCallDateUtility(
                () => DateUtility.dayIsInRange(day, month, year, this.selectedStartDate, this.selectedEndDate),
                false,
                'dayIsInRange'
            );

            const isSameDay = Boolean(
                this.selectedStartDate &&
                this.selectedEndDate &&
                DateUtility.isValidDateString(this.selectedStartDate) &&
                DateUtility.isValidDateString(this.selectedEndDate) &&
                this.selectedStartDate === this.selectedEndDate
            );

            if (isSameDay) {
                isSelected = DateUtility.safeCallDateUtility(
                    () => DateUtility.dayIsGivenDay(day, month, year, this.selectedStartDate),
                    false,
                    'dayIsGivenDay (same day)'
                );
                isSelectedStart = false;
                isSelectedEnd = false;
            } else {
                isSelectedStart = DateUtility.safeCallDateUtility(
                    () => DateUtility.dayIsGivenDay(day, month, year, this.selectedStartDate),
                    false,
                    'dayIsGivenDay (start)'
                );
                isSelectedEnd = DateUtility.safeCallDateUtility(
                    () => DateUtility.dayIsGivenDay(day, month, year, this.selectedEndDate),
                    false,
                    'dayIsGivenDay (end)'
                );
            }
        }

        const dayClassBuilder: ClassBuilder[] = [
            { class: [CalendarTimeElementClass.Day], case: true },
            { class: [CalendarTimeElementClass.Today], case: isToday },
            { class: [CalendarTimeElementClass.Selected], case: isSelected || isSelectedStart || isSelectedEnd },
            { class: [CalendarTimeElementClass.SelectedStart], case: isSelectedStart },
            { class: [CalendarTimeElementClass.SelectedEnd], case: isSelectedEnd },
            {
                class: [CalendarTimeElementClass.Unselectable],
                case: DateUtility.safeCallDateUtility(
                    () => !this.isValidForEarliestBookingDate(day, month, year),
                    false,
                    'dayIsBeforeMinimalDate'
                ),
            },
            {
                class: [CalendarTimeElementClass.Unselectable],
                case: DateUtility.safeCallDateUtility(
                    () =>
                        DateUtility.dayIsClosedDay(day, month, year, this.openingWeekdays, this.specialSelectableDates),
                    false,
                    'dayIsClosedDay'
                ),
            },
            {
                class: [CalendarTimeElementClass.Flexible],
                case: DateUtility.safeCallDateUtility(
                    () => DateUtility.dayIsInRange(day, month, year, this.flexibleStartDate, this.flexibleEndDate),
                    false,
                    'dayIsInRange (flexible)'
                ),
                modifier: [
                    {
                        class: [CalendarTimeElementClass.FlexibleStart],
                        case: DateUtility.safeCallDateUtility(
                            () => DateUtility.dayIsGivenDay(day, month, year, this.flexibleStartDate),
                            false,
                            'dayIsGivenDay (flexible start)'
                        ),
                    },
                    {
                        class: [CalendarTimeElementClass.FlexibleEnd],
                        case: DateUtility.safeCallDateUtility(
                            () => DateUtility.dayIsGivenDay(day, month, year, this.flexibleEndDate),
                            false,
                            'dayIsGivenDay (flexible end)'
                        ),
                    },
                ],
            },
            {
                class: [CalendarTimeElementClass.SelectedRange],
                case: this.mode === 'range' && isInRange && !isSelectedStart && !isSelectedEnd && !isSelected,
            },
        ];

        return dayClassBuilder;
    }

    /**
     * Checks if a date is valid for earliest booking date based on booking day offset.
     * Ensures the date is not before the minimum allowed booking date.
     * @param day Day of the month (1-31)
     * @param month Month (1-12)
     * @param year Year (e.g., 2025)
     * @returns True if the date is valid for booking, false otherwise
     */
    protected isValidForEarliestBookingDate(day: number, month: number, year: number): boolean {
        const bookingDayOffset = Math.max(1, this.bookingDayOffset);

        return !DateUtility.dayIsBeforeMinimalDate(day, month, year, bookingDayOffset);
    }

    /**
     * Renders the weekday header row for the calendar.
     * Displays localized weekday names in short format with long format as title.
     * @returns Template result for the weekday header row
     */
    private renderWeekdays() {
        const weekdayOrder = DateUtility.getWeekdayOrder(this.lang);
        return html`
            <div class="c-calendar__weekdays" role="row" aria-label="Calendar weekdays">
                ${weekdayOrder.map(
                    (day: number) => html`
                        <span
                            class="c-calendar__weekday"
                            role="columnheader"
                            title="${DateUtility.getLocalizedWeekdayName(day, this.lang, 'long')}"
                        >
                            ${DateUtility.getLocalizedWeekdayName(day, this.lang, 'short')}
                        </span>
                    `
                )}
            </div>
        `;
    }

    /**
     * Renders all days for a given month in a calendar grid.
     * Calculates week structure, handles empty cells, and renders day elements.
     * @param month Month (1-12)
     * @param year Year (e.g., 2025)
     * @returns Template result for the month's day grid
     */
    private renderMonthDays(month: number, year: number) {
        try {
            const firstDayOfWeek = DateUtility.getFirstDayOfWeek(this.lang);
            const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
            const daysOffsetSelectedMonth = (firstDayOfMonth - firstDayOfWeek + 7) % 7;
            const daysCountSelectedMonth = new Date(year, month, 0).getDate();
            const weeksCountSelectedMonth = Math.ceil((daysOffsetSelectedMonth + daysCountSelectedMonth) / 7);
            const daysPerWeek = 7;

            const weeks: (number | null)[][] = [[]];
            let currentDay = 1;

            for (let w = 0; w < weeksCountSelectedMonth; w++) {
                const week = [];
                for (let d = 0; d < daysPerWeek; d++) {
                    if (w * daysPerWeek + d >= daysOffsetSelectedMonth && currentDay <= daysCountSelectedMonth) {
                        week[d] = currentDay;
                        currentDay++;
                    } else {
                        week[d] = null;
                    }
                }
                weeks[w] = week;
            }

            return html`
                <div class="c-calendar__days" role="grid" aria-label="Calendar days">
                    ${weeks.flat().map((day) => this.renderDay(day, month, year))}
                </div>
            `;
        } catch (error) {
            console.error('Calendar: Error rendering month days:', error);
            return html` <div class="c-calendar__days error" aria-label="Calendar days">Error loading calendar</div>`;
        }
    }

    /**
     * Handles month selection change from the dropdown selector.
     * Parses the selected month/year value, adjusts for sheet index offset,
     * and updates the active month and year displayed in the calendar.
     * @param e Change event from the month selector dropdown
     * @param sheetIndex Index of the calendar sheet (0 for first sheet, 1 for second sheet in double mode)
     */
    private onMonthChange = (e: Event, sheetIndex: number) => {
        try {
            const select = e.target as HTMLSelectElement;
            const parsed = JSON.parse(select.value);

            const newDate = new Date(parsed.year, parsed.month - 1, 1);
            newDate.setMonth(newDate.getMonth() - sheetIndex);

            this.activeMonth = newDate.getMonth() + 1;
            this.activeYear = newDate.getFullYear();
        } catch (error) {
            console.error('Calendar: Error changing month:', error);
        }
    };

    /**
     * Renders the calendar navigation controls (previous/next month buttons).
     * Determines disabled state based on current date and available month options.
     * @param options Array of available month options
     * @returns Template result for navigation controls
     */
    private renderNavigation(options: SelectOption[]) {
        const lastOption = options[options.length - 1] || { value: '' };
        this.prevDisabled =
            this.activeYear === this.today.getFullYear() && this.activeMonth === this.today.getMonth() + 1;
        this.nextDisabled =
            options.length > 0 && CalendarUtility.getMonthKey(this.activeYear, this.activeMonth) === lastOption.value;

        return html`
            <der-ui-controls
                variant=${DerVariant.Reduced}
                direction=${Direction.Left}
                @click="${this.prevMonth}"
                class="c-calendar__month-control  c-calendar__month-control--prev"
                aria-label="Previous month"
                ?disabled="${this.prevDisabled}"
            ></der-ui-controls>

            <der-ui-controls
                variant=${DerVariant.Reduced}
                direction=${Direction.Right}
                @click="${this.nextMonth}"
                class="c-calendar__month-control  c-calendar__month-control--next"
                aria-label="Next month"
                ?disabled="${this.nextDisabled}"
            ></der-ui-controls>
        `;
    }

    /**
     * Renders the calendar month grids.
     * Generates one or two month grids based on sheets configuration.
     * @param options Array of available month options for dropdown
     * @returns Template result for the calendar months container
     */
    private renderMonths(options: SelectOption[]) {
        const months = [];
        let month: number = this.activeMonth;
        let year: number = this.activeYear;

        for (let i = 0; i < this.sheets; i++) {
            months.push(html`
                <div
                    class="c-calendar__month"
                    role="grid"
                    aria-label="${CalendarUtility.resolveMonthTranslation(this.lang, year, month)} ${year}"
                >
                    <div class="c-calendar__month-title-container">
                        <div class="c-calendar__month-title" role="heading" aria-level="2">
                            ${CalendarUtility.resolveMonthTranslation(this.lang, year, month)} ${year}
                            ${
                                this.monthSelectionDropdown
                                    ? html`
                                          <select
                                              @change="${(e: Event) => this.onMonthChange(e, i)}"
                                              .value="${CalendarUtility.getMonthKey(year, month)}"
                                              class="c-calendar__month-selector"
                                              aria-label="Select month and year"
                                          >
                                              ${options.map(
                                                  (option) => html`
                                                      <option value="${option.value}">${option.label}</option>
                                                  `
                                              )}
                                          </select>
                                      `
                                    : ''
                            }
                        </div>
                    </div>
                    ${this.renderWeekdays()} ${this.renderMonthDays(month, year)}
                </div>
            `);
            month = month === 12 ? 1 : month + 1;
            year = month === 1 ? year + 1 : year;
        }

        return html` <div class="c-calendar__months">${months}</div>`;
    }

    /**
     * Renders the calendar component.
     * Generates month options and renders the calendar structure with navigation and month grids.
     * @returns Template result for the calendar component
     */
    override render() {
        try {
            const options = CalendarUtility.getMonthOptions(
                this.todayString,
                this.flexibleStartDate,
                this.monthDropdownLimit,
                this.lang
            );

            return html`
                <div class="c-calendar" data-mode="${this.mode}" role="application" aria-label="Date picker calendar">
                    ${this.renderNavigation(options)} ${this.renderMonths(options)}
                </div>
            `;
        } catch (error) {
            console.error('Calendar: Error in render:', error);
            return html` <div class="c-calendar error">Calendar could not be loaded</div>`;
        }
    }
}

if (!customElements.get('der-tm-appointment-booking-calendar')) {
    customElements.define('der-tm-appointment-booking-calendar', Calendar);
}
